import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Shield, Zap, Navigation, IndianRupee, Star, Award } from 'lucide-react';

// ─── Data — brand's three accents + ink, nothing else ───────────────────────────

const FEATURES = [
  { icon: Shield,      stat: '100%', label: 'Safe & Verified', sub: 'Background-checked drivers on every trip', color: 'teal' },
  { icon: Zap,         stat: '<30s', label: 'Quick Booking',   sub: 'Ride confirmed in under 30 seconds',        color: 'accent' },
  { icon: Navigation,  stat: 'Live', label: 'GPS Tracking',    sub: 'Real-time location shared with contacts',  color: 'primary' },
  { icon: IndianRupee, stat: 'Low',  label: 'Commission',      sub: 'Drivers keep more of every fare',           color: 'ink' },
];

const COLOR_CLASSES = {
  teal:    { text: 'text-teal-dark dark:text-teal', bg: 'bg-teal-tint dark:bg-teal/10', border: 'border-teal/25' },
  primary: { text: 'text-primary',                   bg: 'bg-primary-tint dark:bg-primary/10', border: 'border-primary/25' },
  accent:  { text: 'text-accent-dark dark:text-accent', bg: 'bg-accent-tint dark:bg-accent/10', border: 'border-accent/25' },
  ink:     { text: 'text-ink dark:text-dark-ink',     bg: 'bg-surface-alt dark:bg-white/10', border: 'border-line dark:border-dark-line' },
};

const AVATARS = [
  { init: 'RK', color: 'teal' },
  { init: 'PS', color: 'primary' },
  { init: 'AS', color: 'accent' },
  { init: 'SD', color: 'ink' },
];

const FeatureCard = ({ f, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const Icon = f.icon;
  const c = COLOR_CLASSES[f.color];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, delay: index * 0.06 }}
      className="p-5 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line"
    >
      <div className={`flex items-center justify-center w-10 h-10 rounded-control mb-4 ${c.bg} border ${c.border}`}>
        <Icon size={18} className={c.text} strokeWidth={2} aria-hidden="true" />
      </div>
      <p className={`text-3xl font-black leading-none mb-2 ${c.text}`}>{f.stat}</p>
      <p className="text-sm font-bold text-ink dark:text-dark-ink mb-1">{f.label}</p>
      <p className="text-xs text-ink-soft leading-snug">{f.sub}</p>
    </motion.div>
  );
};

const HowIsRide = () => {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: '-60px' });
  const trustRef = useRef(null);
  const trustInView = useInView(trustRef, { once: true, margin: '-60px' });

  return (
    <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
      <div className="max-w-[1200px] mx-auto">

        <div ref={headerRef} className="mb-8 max-w-xl">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 bg-primary-tint dark:bg-primary/10 border border-primary/25 text-primary"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            Ride Experience
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, delay: 0.06 }}
            className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-ink dark:text-dark-ink mb-3"
          >
            Safe, quick, <span className="text-primary">hassle-free.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.12 }}
            className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed"
          >
            Every ride is built for safety, speed, and fair pricing.
          </motion.p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {FEATURES.map((f, i) => (
            <FeatureCard key={f.label} f={f} index={i} />
          ))}
        </div>

        <motion.div
          ref={trustRef}
          initial={{ opacity: 0, y: 16 }}
          animate={trustInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.45 }}
          className="flex flex-wrap items-center justify-between gap-5 pt-6 border-t border-line dark:border-dark-line"
        >
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2.5">
              {AVATARS.map((u, i) => {
                const c = COLOR_CLASSES[u.color];
                return (
                  <div
                    key={u.init}
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-black border-2 border-white dark:border-dark-bg ${c.bg} ${c.text}`}
                    style={{ zIndex: AVATARS.length - i }}
                  >
                    {u.init}
                  </div>
                );
              })}
            </div>
            <div>
              <div className="flex items-center gap-0.5 mb-0.5">
                {[1, 2, 3, 4, 5].map((k) => (
                  <Star key={k} size={11} fill="var(--color-accent)" color="var(--color-accent)" />
                ))}
                <span className="text-xs font-bold text-ink dark:text-dark-ink ml-1.5">4.8</span>
              </div>
              <p className="text-sm text-ink-soft dark:text-dark-ink-soft">
                Trusted by <span className="font-bold text-ink dark:text-dark-ink">2K+ riders</span> across India
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-teal-tint dark:bg-teal/10 border border-teal/25">
              <Shield size={13} className="text-teal-dark dark:text-teal" aria-hidden="true" />
              <span className="text-xs font-bold text-teal-dark dark:text-teal">100% Verified Drivers</span>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-3.5 py-2 rounded-full bg-primary-tint dark:bg-primary/10 border border-primary/25">
              <Award size={13} className="text-primary" aria-hidden="true" />
              <span className="text-xs font-bold text-primary">Driver-First Platform</span>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default HowIsRide;
